import { supabase } from '../lib/supabase';

/**
 * Get Excel spreadsheet data for a user
 * @param {string} dataType - 'clients' or 'tags'
 * @returns {Promise<{excelData: Array, fileName: string, columnsConfig: Array}|null>}
 */
export async function getExcelSpreadsheetData(dataType) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return null;
    }

    const { data, error } = await supabase
      .from('excel_spreadsheet_data')
      .select('excel_data, file_name, columns_config')
      .eq('user_id', user.id)
      .eq('data_type', dataType)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No data found
        return null;
      }
      throw error;
    }

    return {
      excelData: data.excel_data || [],
      fileName: data.file_name || '',
      columnsConfig: data.columns_config || null,
    };
  } catch (error) {
    console.error('Error fetching Excel spreadsheet data:', error);
    throw error;
  }
}

/**
 * Save Excel spreadsheet data for a user
 * @param {string} dataType - 'clients' or 'tags'
 * @param {Array} excelData - Array of Excel row data
 * @param {string} fileName - Name of the uploaded file
 * @param {Array} columnsConfig - Column configuration for the table (will be simplified for storage)
 * @returns {Promise<void>}
 */
export async function saveExcelSpreadsheetData(dataType, excelData, fileName, columnsConfig = null) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Limit data size to prevent memory issues (store max 50k rows)
    const limitedData = excelData.slice(0, 50000);
    
    // Simplify columns config for storage (extract only serializable data)
    let simplifiedColumns = null;
    if (columnsConfig && columnsConfig.length > 0) {
      simplifiedColumns = columnsConfig.map(col => ({
        id: col.id,
        accessorKey: col.accessorKey,
        header: typeof col.header === 'function' ? null : col.header,
        enableSorting: col.enableSorting,
        // Don't store cell renderers or other functions
      }));
    }
    
    const { error } = await supabase
      .from('excel_spreadsheet_data')
      .upsert({
        user_id: user.id,
        data_type: dataType,
        file_name: fileName,
        excel_data: limitedData,
        columns_config: simplifiedColumns,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,data_type',
      });

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error saving Excel spreadsheet data:', error);
    throw error;
  }
}

/**
 * Delete Excel spreadsheet data for a user
 * @param {string} dataType - 'clients' or 'tags'
 * @returns {Promise<void>}
 */
export async function deleteExcelSpreadsheetData(dataType) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('excel_spreadsheet_data')
      .delete()
      .eq('user_id', user.id)
      .eq('data_type', dataType);

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error deleting Excel spreadsheet data:', error);
    throw error;
  }
}
